'use client';

import { createContext, useCallback, useContext, useState } from 'react';
import { applicantsApi } from '@/api/applicants';
import { useToast } from '@/hooks/use-toast';
import type {
  Applicant,
  ApplicantFilters,
  FilterOption,
  FilterOptions as FilterOptionsType,
  PaginationState,
} from '@/types/applicant';

// State interfaces
interface ApplicantState {
  data: Applicant[];
  isLoading: boolean;
  error: string | null;
  pagination: PaginationState;
  totalApplicants: number;
}

interface UploadState {
  isCSVUploading: boolean;
  isPDFUploading: boolean;
  progress: { total: number; processed: number } | null;
  error: string | null; // ADDED
}

interface FilterState {
  designations: FilterOption[];
  sources: FilterOption[];
  currentCompanies: FilterOption[];
  invitedForJobs: FilterOption[];
  invitedByEmployees: FilterOption[];
  isLoading: boolean;
  error: string | null;
}

// Context interface
interface ApplicantContextType {
  applicants: ApplicantState;
  upload: UploadState;
  isInviting: boolean;
  isAllSelected: boolean;
  filters: FilterState;
  uploadApplicants: (
    files: File[],
    source: string,
    subsource: string,
    uploadType: 'csv' | 'pdf'
  ) => Promise<void>; // MODIFIED
  fetchApplicants: (
    page?: number,
    limit?: number,
    searchQuery?: string,
    designation?: string,
    additionalFilters?: Partial<Record<string, any>>
  ) => Promise<void>;
  fetchFilters: () => Promise<void>;
  sendInvites: (
    applicantIds: string[],
    jobId: string,
    selectAll?: boolean,
    filters?: Partial<ApplicantFilters>
  ) => Promise<void>;
  updateApplicant: (
    id: string,
    data: {
      name?: string;
      phone?: string;
      countryCode?: string;
      email?: string;
    }
  ) => Promise<Applicant>;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  toggleSelectAll: () => void;
}

// Create context
const ApplicantContext = createContext<ApplicantContextType | undefined>(undefined);

// Initial states
const initialApplicantState: ApplicantState = {
  data: [],
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  },
  totalApplicants: 0,
};

const initialUploadState: UploadState = {
  isCSVUploading: false,
  isPDFUploading: false,
  progress: null,
  error: null, // ADDED
};

const initialFilterState: FilterState = {
  designations: [],
  sources: [],
  currentCompanies: [],
  invitedForJobs: [],
  invitedByEmployees: [],
  isLoading: false,
  error: null,
};

export function ApplicantProvider({ children }: { children: React.ReactNode }) {
  // State
  const [applicants, setApplicants] = useState<ApplicantState>(initialApplicantState);
  const [upload, setUpload] = useState<UploadState>(initialUploadState);
  const [isInviting, setIsInviting] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const { toast } = useToast();

  // Keep track of the current filters for pagination
  const [currentFilters, setCurrentFilters] = useState<{
    searchQuery?: string;
    designation?: string;
    additionalFilters?: Record<string, any>;
  }>({
    searchQuery: '',
    designation: '',
    additionalFilters: {},
  });

  /**
   * Fetch filter options from the API
   */
  const fetchFilters = useCallback(async () => {
    setFilters(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await applicantsApi.getFilters();

      setFilters(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        sources: response.sources || prev.sources,
        designations: response.designations || prev.designations,
        currentCompanies: response.currentCompanies || prev.currentCompanies,
        invitedForJobs: response.invitedForJobs || prev.invitedForJobs,
        invitedByEmployees: response.invitedByEmployees || prev.invitedByEmployees,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load filters';
      setFilters(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, []);

  /**
   * Fetch applicants with filters
   */
  const fetchApplicants = useCallback(
    async (page = 1, limit = 10, searchQuery = '', designation = '', additionalFilters = {}) => {
      // Update current filters for pagination
      setCurrentFilters({
        searchQuery,
        designation,
        additionalFilters,
      });

      setApplicants(prev => ({ ...prev, isLoading: true }));

      try {
        const response = await applicantsApi.list(
          page,
          limit,
          searchQuery,
          designation,
          additionalFilters
        );

        setApplicants({
          data: response.applicants,
          isLoading: false,
          error: null,
          pagination: response.pagination,
          totalApplicants: response.totalApplicants || response.pagination.total,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load data';
        setApplicants(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
      }
    },
    []
  );

  /**
   * Change page and preserve filters
   */
  const setPage = useCallback(
    (page: number) => {
      fetchApplicants(
        page,
        applicants.pagination.limit,
        currentFilters.searchQuery,
        currentFilters.designation,
        currentFilters.additionalFilters
      );
    },
    [applicants.pagination.limit, fetchApplicants, currentFilters]
  );

  /**
   * Change items per page
   */
  const setLimit = useCallback(
    (limit: number) => {
      fetchApplicants(
        1, // Reset to first page
        limit,
        currentFilters.searchQuery,
        currentFilters.designation,
        currentFilters.additionalFilters
      );
    },
    [fetchApplicants, currentFilters]
  );

  /**
   * Upload applicants from files
   */
  const uploadApplicants = async (
    files: File[],
    source: string,
    subsource: string,
    uploadType: 'csv' | 'pdf'
  ): Promise<void> => {
    try {
      setUpload(prev => ({
        ...prev,
        isCSVUploading: uploadType === 'csv' ? true : prev.isCSVUploading,
        isPDFUploading: uploadType === 'pdf' ? true : prev.isPDFUploading,
        error: null,
      }));

      const response = await applicantsApi.upload(files, source, subsource);

      await fetchApplicants();

      toast({
        description: `Successfully uploaded ${response.applicants.length} applicants`,
        variant: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload applicants';
      setUpload(prev => ({
        ...prev,
        error: errorMessage,
        // Keep loading states as they are, they will be reset in finally
        isCSVUploading: prev.isCSVUploading,
        isPDFUploading: prev.isPDFUploading,
      }));
      toast({
        description: errorMessage,
        variant: 'error',
      });
      throw error;
    } finally {
      setUpload(prev => ({
        ...prev,
        isCSVUploading: uploadType === 'csv' ? false : prev.isCSVUploading,
        isPDFUploading: uploadType === 'pdf' ? false : prev.isPDFUploading,
      }));
    }
  };

  /**
   * Send invites to applicants
   */
  const sendInvites = useCallback(
    async (applicantIds: string[], jobId: string, selectAll = false, filters?: Partial<ApplicantFilters>) => {
      setIsInviting(true);

      try {
        await applicantsApi.sendInvites(applicantIds, jobId, selectAll, filters);

        // Refresh the list after sending invites
        fetchApplicants(
          applicants.pagination.currentPage,
          applicants.pagination.limit,
          currentFilters.searchQuery,
          currentFilters.designation,
          currentFilters.additionalFilters
        );

        setIsInviting(false);
      } catch (error) {
        setIsInviting(false);
        throw error;
      }
    },
    [applicants.pagination, fetchApplicants, currentFilters]
  );

  /**
   * Toggle select all applicants
   */
  const toggleSelectAll = useCallback(() => {
    setIsAllSelected(prev => !prev);
  }, []);

  /**
   * Update an applicant's basic information
   */
  const updateApplicant = async (
    id: string,
    data: {
      name?: string;
      phone?: string;
      countryCode?: string;
      email?: string;
    }
  ): Promise<Applicant> => {
    try {
      const response = await applicantsApi.updateApplicant(id, data);

      // Update the applicant in the local state
      setApplicants(prev => ({
        ...prev,
        data: prev.data.map(applicant => (applicant.id === id ? response.applicant : applicant)),
      }));

      toast({
        description: 'Applicant updated successfully',
        variant: 'success',
      });

      return response.applicant;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update applicant';
      toast({
        description: errorMessage,
        variant: 'error',
      });
      throw error;
    }
  };

  // Context value
  const value: ApplicantContextType = {
    applicants,
    upload,
    isInviting,
    isAllSelected,
    filters,
    uploadApplicants,
    fetchApplicants,
    fetchFilters,
    sendInvites,
    updateApplicant,
    setPage,
    setLimit,
    toggleSelectAll,
  };

  return <ApplicantContext.Provider value={value}>{children}</ApplicantContext.Provider>;
}

// Custom hook to use the context
export function useApplicants() {
  const context = useContext(ApplicantContext);

  if (context === undefined) {
    throw new Error('useApplicants must be used within an ApplicantProvider');
  }

  return context;
}
